import { z } from "zod";
/*
 * Relative date ranges for dashboard date filters. Presets resolve against the
 * viewer's calendar day, so a saved "Year to date" filter keeps rolling forward
 * instead of freezing the dates it was created with. The app, native clients,
 * and the Twin share these ids; ranges are inclusive ISO calendar dates.
 */
export const dashboardDatePresetIds = [
    "today",
    "yesterday",
    "last_7_days",
    "last_30_days",
    "last_90_days",
    "week_to_date",
    "month_to_date",
    "quarter_to_date",
    "year_to_date",
    "this_year",
    "previous_year_to_date",
    "year_to_last_month_end",
    "last_month",
    "last_quarter",
    "last_year",
    "last_12_months",
];
export const dashboardDatePresetSchema = z.enum(dashboardDatePresetIds);
export const DASHBOARD_DATE_PRESET_LABELS = {
    today: "Today",
    yesterday: "Yesterday",
    last_7_days: "Last 7 days",
    last_30_days: "Last 30 days",
    last_90_days: "Last 90 days",
    week_to_date: "Week to date",
    month_to_date: "Month to date",
    quarter_to_date: "Quarter to date",
    year_to_date: "Year to date",
    this_year: "This year",
    previous_year_to_date: "Same period last year",
    year_to_last_month_end: "YTD previous month",
    last_month: "Last month",
    last_quarter: "Last quarter",
    last_year: "Last year",
    last_12_months: "Last 12 months",
};
/** Presets offered when a date filter does not choose its own list. */
export const DEFAULT_DASHBOARD_DATE_PRESETS = [
    "today",
    "last_7_days",
    "last_30_days",
    "month_to_date",
    "quarter_to_date",
    "year_to_date",
    "this_year",
    "year_to_last_month_end",
    "previous_year_to_date",
    "last_month",
    "last_quarter",
    "last_year",
];
/**
 * Resolves a preset to an inclusive calendar-date range. `today` is the
 * viewer's local calendar day; weeks start on Monday unless weekStartsOn is 0.
 * Years start in January unless yearStartsOnMonth (1-12) names another month,
 * such as 4 for a financial year from April: the year and quarter presets then
 * count from that month, so "Year to date" in May runs from 1 April.
 */
export function resolveDashboardDatePreset(preset, today, weekStartsOn = 1, yearStartsOnMonth = 1) {
    const range = (start, end) => ({
        start: isoDay(start),
        end: isoDay(end),
    });
    const yearStart = (day) => addMonths({ ...day, day: 1 }, -((day.month - (yearStartsOnMonth - 1) + 12) % 12));
    const thisYearStart = yearStart(today);
    const quarterStart = addMonths({ ...today, day: 1 }, -((today.month - thisYearStart.month + 12) % 3));
    switch (preset) {
        case "today":
            return range(today, today);
        case "yesterday": {
            const yesterday = addDays(today, -1);
            return range(yesterday, yesterday);
        }
        case "last_7_days":
            return range(addDays(today, -6), today);
        case "last_30_days":
            return range(addDays(today, -29), today);
        case "last_90_days":
            return range(addDays(today, -89), today);
        case "week_to_date": {
            const weekday = new Date(Date.UTC(today.year, today.month, today.day)).getUTCDay();
            return range(addDays(today, -((weekday - weekStartsOn + 7) % 7)), today);
        }
        case "month_to_date":
            return range({ ...today, day: 1 }, today);
        case "quarter_to_date":
            return range(quarterStart, today);
        case "year_to_date":
            return range(thisYearStart, today);
        // The whole year, including what is due after today.
        case "this_year":
            return range(thisYearStart, lastDayOfMonth(addMonths(thisYearStart, 11)));
        case "previous_year_to_date":
            return range(addMonths(thisYearStart, -12), clampDay({ year: today.year - 1, month: today.month, day: today.day }));
        case "year_to_last_month_end": {
            // Year to date through the last complete month; in the year's first month that is the whole previous year.
            const end = lastDayOfMonth(addMonths({ ...today, day: 1 }, -1));
            return range(yearStart(end), end);
        }
        case "last_month": {
            const start = addMonths({ ...today, day: 1 }, -1);
            return range(start, lastDayOfMonth(start));
        }
        case "last_quarter": {
            const start = addMonths(quarterStart, -3);
            return range(start, lastDayOfMonth(addMonths(start, 2)));
        }
        case "last_year": {
            const start = addMonths(thisYearStart, -12);
            return range(start, lastDayOfMonth(addMonths(start, 11)));
        }
        case "last_12_months":
            return range(addDays(clampDay({ ...today, year: today.year - 1 }), 1), today);
    }
}
/** The viewer's local calendar day for a Date. */
export function dashboardCalendarDay(date) {
    return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
}
/** The preset whose range equals this one today, so controls can name it. */
export function matchDashboardDatePreset(range, presets, today, weekStartsOn = 1, yearStartsOnMonth = 1) {
    return presets.find((preset) => {
        const resolved = resolveDashboardDatePreset(preset, today, weekStartsOn, yearStartsOnMonth);
        return resolved.start === range.start && resolved.end === range.end;
    }) ?? null;
}
function addDays(day, amount) {
    const date = new Date(Date.UTC(day.year, day.month, day.day + amount));
    return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() };
}
function addMonths(day, amount) {
    const date = new Date(Date.UTC(day.year, day.month + amount, 1));
    return clampDay({ year: date.getUTCFullYear(), month: date.getUTCMonth(), day: day.day });
}
function lastDayOfMonth(day) {
    return { ...day, day: new Date(Date.UTC(day.year, day.month + 1, 0)).getUTCDate() };
}
function clampDay(day) {
    return { ...day, day: Math.min(day.day, lastDayOfMonth(day).day) };
}
function isoDay(day) {
    return [
        String(day.year).padStart(4, "0"),
        String(day.month + 1).padStart(2, "0"),
        String(day.day).padStart(2, "0"),
    ].join("-");
}
